import React, { useEffect, useRef, useState } from 'react';
import { withContent } from '../../hoc/withContext';
import { IContent, IScrollAnimateCallback } from '../../types';
import './Guide.scss';
import BoxOne from './Boxes/BoxOne';
import BoxTwo from './Boxes/BoxTwo';
import BoxThree from './Boxes/BoxThree';
import ScrollAnimation from '../ScrollAnimation/ScrollAnimation';
import Stickylist from './StickyList';
import { sendEvent } from '../../utils';

interface IGuideProps {
  context?: IContent;
}

interface IBoxScrollAnimationProps {
  children: React.ReactNode;
  index: number;
  setCurrentBoxIndex: (index: number) => void;
}

const BoxScrollAnimation = ({ children, index, setCurrentBoxIndex }: IBoxScrollAnimationProps) => (
  <ScrollAnimation
    className='wallet-guide-box-wrapper'
    animateIn='fadeIn'
    animatePreScroll={false}
    key={index}
    offset={400}
    afterAnimatedIn={(visible: IScrollAnimateCallback) => {
      if (visible.onScreen) {
        setCurrentBoxIndex(index);
      }
    }}
  >
    {children}
  </ScrollAnimation>
);

const Guide = ({ context }: IGuideProps) => {
  const { guide } = context;
  const [currentBoxIndex, setCurrentBoxIndex] = useState(0);
  const mobileTopList = useRef<HTMLDivElement>();
  const walletBackgroundRef = useRef<HTMLDivElement>();

  useEffect(() => {
    document.addEventListener('scroll', event => {
      // update background position on scroll
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

      const backgroundYPosition = scrollTop * 0.01;
      walletBackgroundRef.current.style.backgroundPosition = `center ${backgroundYPosition}%`;
    });
  }, []);

  return (
    <section className='wallet-guide'>
      <div className='wallet-guide__background' ref={walletBackgroundRef} />
      <div className='wallet-guide-inner wallet-container'>
        <div className='wallet-guide-inner__left'>
          {guide.showcase.map((box, index) => {
            switch (box.type) {
              case 'add':
                return (
                  <React.Fragment key={index}>
                    <BoxScrollAnimation
                      key={index}
                      index={index}
                      setCurrentBoxIndex={setCurrentBoxIndex}
                    >
                      <Stickylist
                        ctaLink={guide.cta.link}
                        title={guide.content.title}
                        description={guide.content.description}
                        list={guide.content.list}
                        currentBoxIndex={currentBoxIndex}
                        ctaContent={guide.cta.content}
                        ctaTracking={guide.cta.tracking}
                        isMobile={true}
                        showCta={false}
                      />
                      <BoxOne content={box.content} key={index} />
                    </BoxScrollAnimation>
                  </React.Fragment>
                );

              case 'choose':
                return (
                  <BoxScrollAnimation
                    key={index}
                    index={index}
                    setCurrentBoxIndex={setCurrentBoxIndex}
                  >
                    <Stickylist
                      ctaLink={guide.cta.link}
                      title={guide.content.title}
                      description={guide.content.description}
                      list={guide.content.list}
                      currentBoxIndex={currentBoxIndex}
                      ctaContent={guide.cta.content}
                      ctaTracking={guide.cta.tracking}
                      isMobile={true}
                      showCta={false}
                      showHeader={false}
                    />
                    <BoxTwo content={box.content} />
                  </BoxScrollAnimation>
                );

              case 'select':
                return (
                  <BoxScrollAnimation
                    key={index}
                    index={index}
                    setCurrentBoxIndex={setCurrentBoxIndex}
                  >
                    <Stickylist
                      ctaLink={guide.cta.link}
                      title={guide.content.title}
                      description={guide.content.description}
                      list={guide.content.list}
                      currentBoxIndex={currentBoxIndex}
                      ctaContent={guide.cta.content}
                      ctaTracking={guide.cta.tracking}
                      isMobile={true}
                      showCta={false}
                      showHeader={false}
                    />
                    <BoxThree content={box.content} />
                  </BoxScrollAnimation>
                );

              default:
                return null;
            }
          })}
        </div>
        <Stickylist
          ctaLink={guide.cta.link}
          title={guide.content.title}
          description={guide.content.description}
          list={guide.content.list}
          currentBoxIndex={currentBoxIndex}
          ctaContent={guide.cta.content}
          ctaTracking={guide.cta.tracking}
        />
        <div className='mobile-bottom-cta'>
          <a
            href={guide.cta.link}
            className='btn btn__secondary'
            data-alt-text={guide.cta.content}
            onClick={() => sendEvent(guide.cta.tracking)}
          >
            <span>{guide.cta.content}</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default withContent(Guide);
